import { API_BASE } from "../lib/apiOrigin";
import BookingConfirmation, { initialConfirmation, resetAcknowledgments, confirmationReady } from "../components/booking/BookingConfirmation";
import ThemeToggle from "../theme/ThemeToggle";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Calendar, Sun, Sunset, Clock, AlertCircle } from "lucide-react";
import BrandLogo from "../components/BrandLogo";
import { consumePrivateAccess } from "../lib/privateAccess";
import { useLang } from "../i18n/LanguageContext";

const API = API_BASE;

// Localized copy — kept inline (not in translations.js) because this page is
// standalone, reached only from the accept-email link, and needs to work even
// if the language context is not yet mounted.
const COPY = {
  en: {
    loading: "Loading your quote…",
    invalid_title: "Invalid or expired link",
    invalid_msg: "This scheduling link is no longer valid. Please contact Xavier Assembly and we'll help you pick a date.",
    heading: "Pick your install date",
    intro: "Choose the day and time window that works best. We'll confirm the exact arrival time 24 hours before.",
    quote: "Quote",
    service: "Service",
    date_label: "Preferred date",
    window_label: "Time window",
    window_morning: "Morning",
    window_morning_hours: "8am – 12pm",
    window_afternoon: "Afternoon",
    window_afternoon_hours: "12pm – 5pm",
    window_flexible: "Flexible",
    window_flexible_hours: "We'll match your day",
    confirm: "Confirm install date",
    saving: "Saving…",
    picked_prev: "You picked",
    change: "Change",
    success_title: "You're on the calendar",
    success_msg: "Xavier's team will confirm the exact time within 24 hours. See you soon!",
    home: "Back to home",
    error: "Something went wrong. Please try again.",
  },
  pt: {
    loading: "Carregando seu orçamento…",
    invalid_title: "Link inválido ou expirado",
    invalid_msg: "Este link de agendamento não é mais válido. Fale com a Xavier Assembly para escolhermos uma data.",
    heading: "Escolha a data da instalação",
    intro: "Selecione o dia e o horário que preferir. Confirmamos o horário exato 24 horas antes.",
    quote: "Orçamento",
    service: "Serviço",
    date_label: "Data preferida",
    window_label: "Período",
    window_morning: "Manhã",
    window_morning_hours: "8h – 12h",
    window_afternoon: "Tarde",
    window_afternoon_hours: "12h – 17h",
    window_flexible: "Flexível",
    window_flexible_hours: "Combinamos no dia",
    confirm: "Confirmar data",
    saving: "Salvando…",
    picked_prev: "Você escolheu",
    change: "Alterar",
    success_title: "Você está no calendário",
    success_msg: "A equipe da Xavier confirmará o horário exato em até 24 horas. Até breve!",
    home: "Voltar ao site",
    error: "Algo deu errado. Tente novamente.",
  },
  es: {
    loading: "Cargando tu cotización…",
    invalid_title: "Enlace inválido o expirado",
    invalid_msg: "Este enlace de agendamiento ya no es válido. Contacta a Xavier Assembly y te ayudamos a elegir una fecha.",
    heading: "Elige tu fecha de instalación",
    intro: "Selecciona el día y el horario que prefieras. Confirmaremos la hora exacta 24 horas antes.",
    quote: "Cotización",
    service: "Servicio",
    date_label: "Fecha preferida",
    window_label: "Horario",
    window_morning: "Mañana",
    window_morning_hours: "8am – 12pm",
    window_afternoon: "Tarde",
    window_afternoon_hours: "12pm – 5pm",
    window_flexible: "Flexible",
    window_flexible_hours: "Coordinamos ese día",
    confirm: "Confirmar fecha",
    saving: "Guardando…",
    picked_prev: "Elegiste",
    change: "Cambiar",
    success_title: "Estás en el calendario",
    success_msg: "El equipo de Xavier confirmará la hora exacta en 24 horas. ¡Nos vemos pronto!",
    home: "Volver al sitio",
    error: "Algo salió mal. Intenta de nuevo.",
  },
};

// Minimum bookable date = tomorrow (in the customer's local time).
const tomorrowISO = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

// Max bookable date = 12 months out.
const maxDateISO = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const SchedulePage = () => {
  const { qrId } = useParams();
  const [access] = useState(() => consumePrivateAccess("schedule", qrId));
  const token = access.id === qrId ? access.token : "";
  const { lang } = useLang();
  const lock = useRef(false);
  const generation = useRef(0);
  const [ctx, setCtx] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [date, setDate] = useState("");
  const [win, setWin] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    let current = true; generation.current += 1; lock.current = false; setSaving(false);
    setCtx(null); setBooking(null); setDate(""); setWin(""); setConfirmed(false); setErr("");
    if (!token) { setLoading(false); return () => { current = false; }; }
    setLoading(true);
    axios.post(`${API}/schedule/${encodeURIComponent(qrId)}/context`, { token }).then(({ data }) => {
      if (!current) return;
      setCtx(data);
      setBooking(data.booking_confirmation?.enabled ? initialConfirmation(data.booking_confirmation) : null);
      setDate(data.scheduled_date || ""); setWin(data.scheduled_window || "");
      setConfirmed(Boolean(data.already_scheduled) && (!data.booking_confirmation?.enabled || data.booking_confirmation.confirmation_current));
    }).catch(() => { if (current) setErr("invalid"); }).finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [qrId, token]);

  const t = useMemo(() => {
    const language = COPY[ctx?.language] ? ctx.language : (COPY[lang] ? lang : "en");
    const base = COPY[language];
    if (!ctx?.booking_confirmation?.enabled) return base;
    const current = ctx.booking_confirmation.confirmation_current;
    const labels = {
      en: { heading: "Review your installation booking", intro: "Choose a preferred date and window. Xavier will set an exact appointment for you to review and confirm before dispatch.", confirm: "Save booking confirmation", success_title: current ? "Address and appointment confirmed" : "Preference and address saved", success_msg: current ? "Your confirmed address and exact appointment are recorded. Dispatch still requires the team's other approval checks." : "Your preferred window is recorded. Review and confirm the exact appointment here when Xavier sets it." },
      pt: { heading: "Revise o agendamento da instalação", intro: "Escolha a data e o período de preferência. A Xavier definirá um horário exato para você revisar e confirmar antes do envio do profissional.", confirm: "Salvar confirmação", success_title: current ? "Endereço e horário confirmados" : "Preferência e endereço salvos", success_msg: current ? "Seu endereço e horário exato foram confirmados. O envio do profissional ainda depende das demais aprovações da equipe." : "Seu período de preferência foi registrado. Revise e confirme aqui o horário exato quando a Xavier o definir." },
      es: { heading: "Revisa tu cita de instalación", intro: "Elige una fecha y franja preferidas. Xavier fijará una cita exacta para que la revises y confirmes antes de enviar al profesional.", confirm: "Guardar confirmación", success_title: current ? "Dirección y cita confirmadas" : "Preferencia y dirección guardadas", success_msg: current ? "Tu dirección y cita exacta están confirmadas. El envío del profesional aún requiere las demás aprobaciones del equipo." : "Registramos tu franja preferida. Revisa y confirma aquí la cita exacta cuando Xavier la fije." },
    };
    return { ...base, ...labels[language] };
  }, [ctx, lang]);
  const minDate = useMemo(tomorrowISO, []);
  const maxDate = useMemo(maxDateISO, []);

  const slotChanged = date !== (ctx?.scheduled_date || "") || win !== (ctx?.scheduled_window || "");
  const exactRequired = Boolean(ctx?.booking_confirmation?.exact_appointment && !slotChanged);
  const bookingReady = !ctx?.booking_confirmation?.enabled || confirmationReady(booking, exactRequired);

  const submit = async (e) => {
    e.preventDefault();
    if (!token || !ctx || !date || !win || !bookingReady || lock.current) return;
    const activeGeneration = generation.current;
    lock.current = true;
    setSaving(true);
    setErr("");
    try {
      const response = await axios.post(`${API}/schedule/${encodeURIComponent(qrId)}`, { token, date, window: win, ...(ctx.booking_confirmation?.enabled ? { booking_confirmation: booking } : {}) });
      if (generation.current !== activeGeneration) return;
      const canonicalDate = response.data.scheduled_date || date;
      const canonicalWindow = response.data.scheduled_window || win;
      setDate(canonicalDate); setWin(canonicalWindow);
      if (response.data.booking_confirmation) {
        setCtx((previous) => ({ ...previous, scheduled_date: canonicalDate, scheduled_window: canonicalWindow, booking_confirmation: response.data.booking_confirmation }));
        setBooking(initialConfirmation(response.data.booking_confirmation));
      }
      setConfirmed(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      if (generation.current !== activeGeneration) return;
      if (e.response?.status === 409) {
        try {
          const { data } = await axios.post(`${API}/schedule/${encodeURIComponent(qrId)}/context`, { token });
          if (generation.current !== activeGeneration) return;
          setCtx(data); setBooking(data.booking_confirmation?.enabled ? initialConfirmation(data.booking_confirmation) : null);
          setDate(data.scheduled_date || ""); setWin(data.scheduled_window || "");
        } catch (_) { /* Keep the form and let the user retry. */ }
      }
      if (generation.current !== activeGeneration) return;
      setErr(typeof e.response?.data?.detail === "string" ? e.response.data.detail : t.error);
    } finally {
      if (generation.current === activeGeneration) { lock.current = false; setSaving(false); }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] text-white flex items-center justify-center px-6">
        <div className="text-zinc-400 text-sm uppercase tracking-[0.2em]">{t.loading}</div>
      </div>
    );
  }

  if (err === "invalid" || !ctx) {
    return (
      <div className="min-h-screen bg-[#09090b] text-white flex items-center justify-center px-6">
        <div className="max-w-md text-center border border-red-500/30 bg-red-500/10 p-8">
          <AlertCircle size={38} className="mx-auto text-red-400 mb-4" />
          <h1 className="font-display font-black text-2xl mb-3">{t.invalid_title}</h1>
          <p className="text-zinc-300 text-sm leading-relaxed mb-6">{t.invalid_msg}</p>
          <Link
            to="/"
            className="inline-block border border-white/20 hover:border-sky-400 text-white text-xs uppercase tracking-[0.2em] font-bold px-5 py-3"
          >
            {t.home}
          </Link>
        </div>
      </div>
    );
  }

  const windowOptions = [
    { id: "morning", label: t.window_morning, hours: t.window_morning_hours, Icon: Sun },
    { id: "afternoon", label: t.window_afternoon, hours: t.window_afternoon_hours, Icon: Sunset },
    { id: "flexible", label: t.window_flexible, hours: t.window_flexible_hours, Icon: Clock },
  ];

  return (
    <div className="min-h-screen bg-[#09090b] text-white" data-testid="schedule-page">
      <header className="border-b border-white/10">
        <div className="max-w-3xl mx-auto px-6 py-6 flex items-center justify-between">
          <BrandLogo size="h-9 w-9" />
          <div className="text-zinc-400 text-xs uppercase tracking-[0.2em]">
            #{ctx.quote_short_id}
          </div>
        </div>
      <ThemeToggle /></header>

      <main className="max-w-3xl mx-auto px-6 py-10 sm:py-14">
        {confirmed ? (
          <div
            data-testid="schedule-success"
            className="border border-emerald-400/40 bg-emerald-500/10 p-8 sm:p-10 text-center"
          >
            <CheckCircle2 size={54} className="mx-auto text-emerald-400 mb-5" />
            <h1 className="font-display font-black text-3xl sm:text-4xl mb-3">{t.success_title}</h1>
            <p className="text-zinc-300 text-base leading-relaxed mb-6 max-w-md mx-auto">
              {t.success_msg}
            </p>
            <div className="inline-flex flex-col items-start gap-1 border border-white/10 bg-zinc-950/30 px-6 py-4 text-left">
              <span className="text-[10px] uppercase tracking-[0.2em] text-emerald-300 font-bold">
                {t.picked_prev}
              </span>
              <span className="font-display font-bold text-lg" data-testid="schedule-confirmed-date">
                {date}
                {" · "}
                {win === "morning"
                  ? `${t.window_morning} · ${t.window_morning_hours}`
                  : win === "afternoon"
                  ? `${t.window_afternoon} · ${t.window_afternoon_hours}`
                  : `${t.window_flexible}`}
              </span>
            </div>
            <div className="mt-8">
              {ctx.booking_confirmation?.enabled && !ctx.booking_confirmation.confirmation_current && <p className="mb-4 text-amber-200">{({ en: "Exact appointment confirmation is pending. Visit-fee consent is not yet applicable.", pt: "A confirmação do horário exato está pendente. O consentimento da taxa de visita ainda não se aplica.", es: "La confirmación de la hora exacta está pendiente. El consentimiento de la tarifa aún no se aplica." })[ctx.language || lang] || "Exact appointment confirmation is pending."}</p>}
              <button
                type="button"
                onClick={() => { setConfirmed(false); if (booking) setBooking(resetAcknowledgments(booking)); }}
                data-testid="schedule-edit"
                className="text-sky-300 hover:text-sky-200 text-xs uppercase tracking-[0.2em] font-bold underline"
              >
                {t.change}
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="h-px w-12 bg-sky-400"></span>
                <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-sky-300">
                  {t.quote} #{ctx.quote_short_id}
                </span>
              </div>
              <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight mb-3">
                {t.heading}
              </h1>
              <p className="text-zinc-400 text-base leading-relaxed max-w-xl">{t.intro}</p>
              {(ctx.service || ctx.customer_name) && (
                <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm">
                  {ctx.customer_name && (
                    <span className="text-zinc-300">
                      <span className="text-zinc-500 mr-2">{"→"}</span>
                      {ctx.customer_name}
                    </span>
                  )}
                  {ctx.service && (
                    <span className="text-zinc-300">
                      <span className="text-zinc-500 mr-2">{t.service}:</span>
                      {ctx.service}
                    </span>
                  )}
                </div>
              )}
            </div>

            <div className="border border-white/10 bg-zinc-950 p-6 sm:p-8 space-y-8">
              <div>
                <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-300 mb-3 block">
                  <Calendar size={14} className="inline mr-2 -mt-0.5 text-sky-400" />
                  {t.date_label}
                </label>
                <input
                  data-testid="schedule-date-input"
                  type="date"
                  required
                  min={exactRequired ? ctx.scheduled_date : minDate}
                  disabled={saving}
                  max={maxDate}
                  value={date}
                  onChange={(e) => { setDate(e.target.value); if (booking) setBooking(resetAcknowledgments(booking)); }}
                  className="w-full sm:w-auto bg-transparent border-b border-white/20 focus:border-sky-400 text-white text-xl py-3 outline-none tabular-nums"
                />
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-300 mb-3 block">
                  {t.window_label}
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {windowOptions.map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      disabled={saving}
                      onClick={() => { setWin(opt.id); if (booking) setBooking(resetAcknowledgments(booking)); }}
                      data-testid={`schedule-window-${opt.id}`}
                      className={`text-left p-4 border transition-colors ${
                        win === opt.id
                          ? "border-sky-400 bg-sky-500/10"
                          : "border-white/10 hover:border-white/25"
                      }`}
                    >
                      <opt.Icon size={22} className={`mb-2 ${win === opt.id ? "text-sky-400" : "text-zinc-400"}`} />
                      <div className="font-display font-bold text-white text-base">{opt.label}</div>
                      <div className="text-zinc-400 text-xs mt-0.5">{opt.hours}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <BookingConfirmation context={ctx.booking_confirmation} value={booking} onChange={setBooking} language={ctx.language || lang} slotChanged={slotChanged} disabled={saving} />

            {err && (
              <div className="border border-red-500/30 bg-red-500/10 px-4 py-3 text-red-300 text-sm">
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={saving || !date || !win || !bookingReady}
              data-testid="schedule-submit"
              className="btn-primary text-base px-8 py-4 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {saving ? t.saving : t.confirm}
            </button>
          </form>
        )}
      </main>
    </div>
  );
};

export default SchedulePage;
